/**
 * AreaPitchAzimu.js
 * 
 * This module contains utility functions for calculating geometric properties of polygons in 3D space,
 * including pitch angle, area, and azimuth. It's primarily used for roof and terrain analysis in the
 * SightFlight Designer application.
 * 
 * The module leverages THREE.js for 3D vector calculations and proj4 for geographic coordinate transformations.
 */

import * as THREE from 'three'
import proj4 from "proj4";
import {VARS} from "../Global/variables";
import {DEV_MOD} from "../../helpers/CONSTANT";
import {sceneManager} from "../SetUps/SceneManager";

/**
 * Calculates the pitch (inclination angle) of a polygon surface
 * 
 * @param {Array} polygon - Array of objects with point1 property (THREE.Vector3)
 * @returns {Object|number} Object containing pitch_angle, p1, and p2 points or 0 if calculation is impossible
 */
export function calculatePitch(polygon){
    // Define the horizontal plane (normal vector pointing up in z-direction)
    const plane1 = new THREE.Plane(new THREE.Vector3(0,0,1)); // x-y plane
    // Create an empty plane that will be defined by the polygon
    const plane2 = new THREE.Plane(); // segment plane

    // Find the highest and lowest points in the polygon (based on z-coordinate)
    let maxZ = polygon[0].point1.clone();
    let minZ = polygon[0].point1.clone();
    for (let i=1; i < polygon.length; i++) {
        if (polygon[i].point1.z > maxZ.z) {
            maxZ = polygon[i].point1.clone();
        }
        if (polygon[i].point1.z < minZ.z) {
            minZ = polygon[i].point1.clone();
        }
    }

    // Find a third point to define the plane that maximizes the angle
    // We need a point that's not collinear with the min and max points
    const restPoints = polygon.filter((point) => !point.point1.equals(maxZ) && !point.point1.equals(minZ));
    const minToMax = new THREE.Vector3().subVectors(minZ, maxZ);
    
    // Find the point that creates the largest angle with the min-to-max vector
    let point = 0;
    let angle = minToMax.angleTo(new THREE.Vector3().subVectors(restPoints[0].point1, maxZ));
    for (let i = 1; i < restPoints.length; i++) {
        let newAngle = minToMax.angleTo(new THREE.Vector3().subVectors(restPoints[i].point1, maxZ));
        if (newAngle > angle) {
            angle = newAngle;
            point = i;
        }
    }
    
    // Create a plane from the three chosen points
    plane2.setFromCoplanarPoints(maxZ, minZ, restPoints[point].point1);

    // Get the normal vectors of both planes
    const n1 = plane1.normal;
    const n2 = plane2.normal;
    
    // Calculate the direction of the intersection of the two planes
    const dir = new THREE.Vector3().crossVectors(n1, n2); // intersection line direction
    if (dir.length() === 0) {
        // If the direction vector is zero, the planes are parallel or coincident
        return 0;
    }
    
    // Calculate vectors for determining the angle
    const run = new THREE.Vector3().crossVectors(n1, dir); // Vector in the horizontal plane perpendicular to dir
    const hyp = new THREE.Vector3().crossVectors(n2, dir); // Vector in the polygon plane perpendicular to dir

    // Calculate the pitch angle in degrees
    let pitch = radians_to_degrees(hyp.angleTo(run)).toFixed(0);
    if (pitch > 90) {
        // Adjust pitch to be acute or right angle
        pitch = 180 - pitch;
    }

    // Create a line along the intersection direction from the minimum height point
    const v = new THREE.Vector3().addVectors(minZ, dir.multiplyScalar(2));
    const line = new THREE.Line3(minZ, v);
    const v2 = new THREE.Vector3();
    
    // Find the closest point on the line to the maximum height point
    line.closestPointToPoint(maxZ, false, v2);

    // Return the pitch angle and the two points that define the steepest slope
    return {pitch_angle: pitch, p1: maxZ.clone(), p2: v2.clone()};
}

/**
 * Converts radians to degrees
 * 
 * @param {number} rad - Angle in radians
 * @returns {number} Angle in degrees
 */
export function radians_to_degrees(rad) {
    return (rad * 180.0) / Math.PI;
}

/**
 * Calculates the area of a 3D polygon
 * 
 * Uses the cross product method to find the area of a polygon in 3D space
 * The result is multiplied by 10.7639 to convert from square meters to square feet
 * 
 * @param {Array} polygon - Array of objects with point1 property (THREE.Vector3)
 * @returns {number} Area of the polygon in square feet
 */
export function getPolyArea(polygon) {
    // Convert the polygon format to simple arrays of coordinates
    let poly = [];
    for (let i = 0; i < polygon.length; i++) {
        poly.push([polygon[i].point1.x, polygon[i].point1.y, polygon[i].point1.z]);
    }
    
    // A polygon needs at least 3 points to have an area
    if (poly.length < 3) {
        return 0;
    } else {
        // Initialize the total for summing cross products
        let total = [0, 0, 0]
        
        // Sum the cross products of adjacent vertices
        for (let i = 0; i < poly.length; i++) {
            var vi1 = poly[i];
            var vi2 = (i === poly.length - 1) ? poly[0] : poly[i + 1]; // Wrap around to the first point
            
            let prod = cross(vi1, vi2);
            total[0] = total[0] + prod[0];
            total[1] = total[1] + prod[1];
            total[2] = total[2] + prod[2];
        }
        
        // Dot product with the unit normal gives twice the area of the polygon
        let result = dot(total, unit_normal(poly[0], poly[1], poly[2]));
        
        // Return the absolute value of the area (divided by 2) and convert to square feet
        return Math.abs(result / 2) * 10.7639;
    }
}

/**
 * Calculates the dot product of two 3D vectors
 * 
 * @param {Array} a - First vector as [x, y, z]
 * @param {Array} b - Second vector as [x, y, z]
 * @returns {number} Dot product value
 */
function dot(a, b) {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

/**
 * Calculates the cross product of two 3D vectors
 * 
 * @param {Array} a - First vector as [x, y, z]
 * @param {Array} b - Second vector as [x, y, z]
 * @returns {Array} Cross product vector as [x, y, z]
 */
function cross(a, b) {
    let x = (a[1] * b[2]) - (a[2] * b[1]);
    let y = (a[2] * b[0]) - (a[0] * b[2]);
    let z = (a[0] * b[1]) - (a[1] * b[0]);
    return [x, y, z];
}

/**
 * Calculates the unit normal vector to a plane defined by three points
 * 
 * @param {Array} a - First point as [x, y, z]
 * @param {Array} b - Second point as [x, y, z]
 * @param {Array} c - Third point as [x, y, z]
 * @returns {Array} Unit normal vector as [x, y, z]
 */
function unit_normal(a, b, c) {
    // Calculate components of the normal vector using determinants
    let x = det([
        [1, a[1], a[2]],
        [1, b[1], b[2]],
        [1, c[1], c[2]]
    ]);
    let y = det([
        [a[0], 1, a[2]],
        [b[0], 1, b[2]],
        [c[0], 1, c[2]]
    ]);
    let z = det([
        [a[0], a[1], 1],
        [b[0], b[1], 1],
        [c[0], c[1], 1]
    ]);
    
    // Calculate the magnitude of the normal vector
    let magnitude = Math.pow(Math.pow(x, 2) + Math.pow(y, 2) + Math.pow(z, 2), 0.5);
    
    // Return the normalized (unit) vector
    return [x / magnitude, y / magnitude, z / magnitude];
}

/**
 * Calculates the determinant of a 3x3 matrix
 * 
 * @param {Array} a - 3x3 matrix represented as array of arrays
 * @returns {number} Determinant value
 */
function det(a) {
    return a[0][0] * a[1][1] * a[2][2] + 
           a[0][1] * a[1][2] * a[2][0] + 
           a[0][2] * a[1][0] * a[2][1] - 
           a[0][2] * a[1][1] * a[2][0] - 
           a[0][1] * a[1][0] * a[2][2] - 
           a[0][0] * a[1][2] * a[2][1];
}

/**
 * Calculates the azimuth (compass bearing) between two points
 * 
 * Converts local coordinates to geographic coordinates using the project's
 * coordinate system, then calculates the true bearing using the Haversine formula
 * 
 * @param {THREE.Vector3} P1 - Starting point
 * @param {THREE.Vector3} P2 - Ending point
 * @returns {number} Azimuth in degrees (0-360°, where 0° is North)
 */
export function getAzimuth(P1, P2) {
    // Get project coordinate system data
    let {originXY, proj} = getModelData();

    // Convert local coordinates to longitude/latitude
    let lonlat1 = proj.inverse([originXY[0] - P1.x, originXY[1] - P1.y]);
    let lonlat2 = proj.inverse([originXY[0] - P2.x, originXY[1] - P2.y]);
    
    // Convert to radians for calculation
    let lat1 = degrees_to_radians(lonlat1[1]);
    let lat2 = degrees_to_radians(lonlat2[1]);
    let lon1 = degrees_to_radians(lonlat1[0]);
    let lon2 = degrees_to_radians(lonlat2[0]);
    
    // Calculate the longitude difference
    let deltalon = lon2 - lon1;
    
    // Calculate the y and x components for the bearing
    let y2 = Math.sin(deltalon) * Math.cos(lat2);
    let x2 = (Math.cos(lat1) * Math.sin(lat2)) - (Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltalon));
    
    // Calculate the azimuth using arctangent of y/x and convert to degrees
    let azimth = radians_to_degrees(Math.atan2(y2, x2));
    
    // Adjust negative angles to the 0-360° range
    if (azimth < 0) {
        azimth = 360 + azimth;
    }
    
    return azimth;
}

/**
 * Gets the model data for coordinate transformations
 * 
 * Either returns default data for development mode or gets the data from the current project
 * 
 * @returns {Object} Object containing originXY coordinates and projection function
 */
function getModelData() {
    let projection = null;
    let originXY = null;
    
    // Use different data sources based on development mode
    if(DEV_MOD){
        // Default coordinates and projection for development mode (Fionia model)
        originXY = [681184, 4585677]
        projection= "+proj=utm +zone=19 +datum=WGS84 +units=m +no_defs +type=crs"
    }
    else if (VARS.Current_Project?.co_ordinates && VARS.Current_Project?.projection) {
        // Get coordinates and projection from the current project
        let coordinates = VARS.Current_Project?.co_ordinates?.split(" ");
        let x = parseInt(coordinates[0]);
        let y = parseInt(coordinates[1]);
        originXY = [x, y]
        projection = VARS.Current_Project.projection
    }

    // Initialize the proj4 projection function
    let proj = proj4(projection)
    return {originXY, proj};
}

/**
 * Converts degrees to radians
 * 
 * @param {number} degrees - Angle in degrees
 * @returns {number} Angle in radians
 */
function degrees_to_radians(degrees) {
    var pi = Math.PI;
    return degrees * (pi / 180);
}